import React, { useEffect, useState } from "react";
import { api, downloadFile } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileSpreadsheet, FileText, Eye, Printer } from "lucide-react";
import { toast } from "sonner";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

const BUCKETS = [
  { key: "all", label: "Усі категорії" },
  { key: "clinic", label: "Клініка" },
  { key: "business_trip", label: "Відрядження" },
  { key: "external_doctor", label: "Зовнішні лікарі" },
];

// Reports that support the bucket filter
const BUCKET_ENABLED = new Set(["salary_by_employee", "salary_by_role"]);

export default function Reports() {
  const [types, setTypes] = useState([]);
  const [active, setActive] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(currentMonth());
  const [bucket, setBucket] = useState("all");

  useEffect(() => { api.get("/reports/types").then((r) => setTypes(r.data)); }, []);

  const qs = (t) => {
    const p = new URLSearchParams();
    if (t.value === "salary_monthly_breakdown") p.set("month", month);
    if (BUCKET_ENABLED.has(t.value) && bucket !== "all") p.set("bucket", bucket);
    const s = p.toString();
    return s ? `?${s}` : "";
  };
  const fileSuffix = (t) => {
    const parts = [];
    if (t.value === "salary_monthly_breakdown") parts.push(month);
    if (BUCKET_ENABLED.has(t.value) && bucket !== "all") parts.push(bucket);
    return parts.length ? `_${parts.join("_")}` : "";
  };

  const view = async (t) => {
    setActive(t.value); setLoading(true);
    try { const r = await api.get(`/reports/${t.value}${qs(t)}`); setData(r.data); }
    catch (e) { toast.error("Не вдалося завантажити звіт"); }
    setLoading(false);
  };
  const downloadExcel = async (t) => {
    try {
      const r = await api.get(`/reports/${t.value}/excel${qs(t)}`, { responseType: "blob" });
      downloadFile(r.data, `${t.value}${fileSuffix(t)}.xlsx`);
    } catch (e) { toast.error("Помилка експорту"); }
  };
  const downloadPdf = async (t) => {
    try {
      const r = await api.get(`/reports/${t.value}/pdf${qs(t)}`, { responseType: "blob" });
      downloadFile(r.data, `${t.value}${fileSuffix(t)}.pdf`);
    } catch (e) { toast.error("Помилка експорту"); }
  };

  const printReport = () => {
    if (!data) return;
    const w = window.open("", "_blank");
    if (!w) return;
    const rows = data.rows.map((row, i) =>
      `<tr>${row.map((c, j) => `<td class="${typeof c === "number" ? "r" : ""}">${
        typeof c === "number" && c > 100 ? c.toLocaleString("uk-UA") : String(c)
      }</td>`).join("")}</tr>`
    ).join("");
    const bucketNote = data.bucket && data.bucket !== "all"
      ? ` · Категорія: <b>${data.bucket_label}</b>` : "";
    w.document.write(`<!doctype html><html><head><meta charset="utf-8"/><title>${data.title}</title>
    <style>
      body{font-family:'DejaVu Sans','Segoe UI',Arial,sans-serif;margin:20px;color:#0F2027}
      h1{margin:0 0 4px}
      .sub{color:#334155;margin-bottom:14px;font-size:13px}
      table{width:100%;border-collapse:collapse;font-size:11px}
      th{background:#0F2027;color:#fff;padding:6px;text-align:left}
      td{border:1px solid #CBD5E1;padding:5px 6px}
      td.r{text-align:right}
      tr:nth-child(even) td{background:#F8FAFC}
      tr:last-child td{font-weight:600;background:#E2E8F0}
      @media print{.no-print{display:none}}
      button{padding:6px 12px;margin-right:6px;cursor:pointer}
    </style></head><body>
      <div class="no-print" style="margin-bottom:12px">
        <button onclick="window.print()">🖨 Друкувати</button>
        <button onclick="window.close()">Закрити</button>
      </div>
      <h1>GV Analytics — ${data.title}</h1>
      <div class="sub">Записів: <b>${data.rows.length}</b>${bucketNote}${data.month ? ` · Місяць: <b>${data.month}</b>` : ""}</div>
      <table>
        <thead><tr>${data.headers.map((h) => `<th>${h}</th>`).join("")}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </body></html>`);
    w.document.close();
  };

  return (
    <div data-testid="reports-page" className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-3xl font-bold text-[#0F2027]">Звіти</h1>
          <p className="text-sm text-slate-500">Перегляд та експорт у Excel / PDF</p>
        </div>
        <div className="flex items-end gap-3">
          <div>
            <Label className="text-xs">Категорія (для звітів по ЗП)</Label>
            <Select value={bucket} onValueChange={setBucket}>
              <SelectTrigger className="w-48" data-testid="reports-bucket"><SelectValue /></SelectTrigger>
              <SelectContent>
                {BUCKETS.map((b) => <SelectItem key={b.key} value={b.key}>{b.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Місяць (для місячних звітів)</Label>
            <Input type="month" data-testid="reports-month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {types.map((t) => (
          <Card key={t.value} className="gv-card" data-testid={`report-card-${t.value}`}>
            <CardContent className="p-5">
              <div className="font-semibold text-[#0F2027] mb-1">{t.label}</div>
              {BUCKET_ENABLED.has(t.value) && bucket !== "all" && (
                <div className="inline-block px-2 py-0.5 mb-2 rounded text-[11px] font-medium bg-teal-50 text-teal-700 border border-teal-200">
                  {BUCKETS.find((b) => b.key === bucket)?.label}
                </div>
              )}
              <div className="flex gap-2 flex-wrap mt-2">
                <Button size="sm" variant="outline" onClick={() => view(t)} data-testid={`report-view-${t.value}`}>
                  <Eye className="h-4 w-4 mr-1" /> Переглянути
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadExcel(t)} data-testid={`report-excel-${t.value}`}>
                  <FileSpreadsheet className="h-4 w-4 mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadPdf(t)} data-testid={`report-pdf-${t.value}`}>
                  <FileText className="h-4 w-4 mr-1" /> PDF
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {loading && <div className="text-slate-500">Завантаження...</div>}

      {data && (
        <Card className="gv-card">
          <CardContent className="p-0">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3">
              <div>
                <div className="font-semibold text-[#0F2027]">{data.title}</div>
                <div className="text-xs text-slate-500">
                  Записів: {data.rows.length}
                  {data.bucket && data.bucket !== "all" && ` · Категорія: ${data.bucket_label}`}
                  {data.month && ` · Місяць: ${data.month}`}
                </div>
              </div>
              <Button size="sm" variant="outline" onClick={printReport} data-testid="report-print">
                <Printer className="h-4 w-4 mr-1" /> Друкувати
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-600 sticky top-0 z-10">
                  <tr>{data.headers.map((h, i) => <th key={i} className="text-left p-3">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {data.rows.map((row, i) => (
                    <tr key={i} className={`border-t border-slate-100 ${row[0] === "РАЗОМ" ? "bg-slate-100 font-semibold" : ""}`}>
                      {row.map((c, j) => (
                        <td key={j} className={`p-3 ${typeof c === "number" ? "text-right tabular-nums" : ""}`}>
                          {typeof c === "number" && c > 100 ? c.toLocaleString("uk-UA") : String(c)}
                        </td>
                      ))}
                    </tr>
                  ))}
                  {data.rows.length === 0 && <tr><td colSpan={data.headers.length} className="p-8 text-center text-slate-400">Немає даних</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
